import { LotEventTypes, LotStatus } from '@parallel-fluidics/shared/constants';
import { lotEventToFirestore, lotFromFirestore } from '@parallel-fluidics/shared/models/lots';
import {
  collection,
  doc,
  getFirestore,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';

/**
 * Adjust a lot's remaining quantity by a signed delta and log an
 * `adjusted` event in the same transaction. Quantities are advisory — an
 * adjustment never blocks work. Status side effects, since Empty is
 * derived from the count: adjusting an Empty bag above zero reopens it,
 * and adjusting a bag to zero or below marks it Empty (each with a
 * `status_changed` event) — except Quarantined bags, which keep their
 * status no matter what the count does.
 */
export default async function adjustLotQty({
  lotId, deltaQty, reason, user,
}) {
  const db = getFirestore();
  const lotRef = doc(db, 'lots', lotId);

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(lotRef);
    if (!snap.exists()) throw new Error('Lot not found');
    const lot = lotFromFirestore(snap);
    const newRemaining = (lot.remainingQty ?? 0) + deltaQty;

    const patch = { remainingQty: newRemaining };
    const reopened = lot.status === LotStatus.EMPTY && newRemaining > 0;
    // Quarantined is sticky: deriving Empty here would make the bag
    // consumable again (Empty only warns at scan; Quarantined rejects).
    const emptied = ![LotStatus.EMPTY, LotStatus.QUARANTINED].includes(lot.status)
      && newRemaining <= 0;
    if (reopened || emptied) {
      patch.status = reopened ? LotStatus.OPEN : LotStatus.EMPTY;
      patch.statusChangedAt = serverTimestamp();
    }
    transaction.update(lotRef, patch);

    transaction.set(doc(collection(lotRef, 'events')), lotEventToFirestore({
      type: LotEventTypes.ADJUSTED,
      deltaQty,
      reason,
      _createdBy: user,
    }, serverTimestamp));
    if (reopened || emptied) {
      transaction.set(doc(collection(lotRef, 'events')), lotEventToFirestore({
        type: LotEventTypes.STATUS_CHANGED,
        reason: reopened
          ? `${LotStatus.OPEN} — quantity adjusted above zero`
          : `${LotStatus.EMPTY} — quantity adjusted to zero or below`,
        _createdBy: user,
      }, serverTimestamp));
    }
  });
}
